import React, { useRef, useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Img } from 'react-image';
import toast from 'components/Basic/Toast';
import { getImageLink } from 'utilities/common';
import { CampaignImageType } from 'core/worldCup/raffleImages/types';
import RaffleImagePreviewModal from './RaffleImagePreviewModal';
import DevicePreview from './DevicePreview';
import { VALID_IMAGE_FORMATS } from 'utilities/constants';
import { validateImageBytes } from 'utilities/validateImageFile';

export type AspectRatio = 'landscape' | 'portrait';

type RaffleImageSectionProps = {
  title: string;
  type: CampaignImageType;
  aspectRatio: AspectRatio;
  requiredWidth?: number;
  requiredHeight?: number;
  helperText?: string;
  currentImage?: string;
  onChange: (file: File | null) => void;
};

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  const objectUrl = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve({ width: image.width, height: image.height });
      URL.revokeObjectURL(objectUrl);
    };

    image.onerror = () => {
      reject(new Error('Could not load image dimensions'));
      URL.revokeObjectURL(objectUrl);
    };

    image.src = objectUrl;
  });
};

const RaffleImageSection: React.FC<RaffleImageSectionProps> = ({
  title,
  type,
  aspectRatio,
  requiredWidth,
  requiredHeight,
  helperText,
  currentImage,
  onChange,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();

  const [previewSrc, setPreviewSrc] = useState<string | null>(currentImage ? getImageLink(currentImage) : null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    setPreviewSrc(currentImage ? getImageLink(currentImage) : null);
  }, [currentImage]);

  const handleOpenFilePicker = () => {
    fileRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const clearInput = () => {
      event.target.value = '';
    };

    const file = event.target.files?.[0];
    if (!file) {
      clearInput();
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error({ title: intl.formatMessage({ id: 'Image size should be less than 2MB' }) });
      clearInput();
      return;
    }

    if (!VALID_IMAGE_FORMATS.includes(file.type)) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      clearInput();
      return;
    }

    const isValidImage = await validateImageBytes(file);
    if (!isValidImage) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      clearInput();
      return;
    }

    if (requiredWidth && requiredHeight) {
      try {
        const { width, height } = await getImageDimensions(file);
        if (width !== requiredWidth || height !== requiredHeight) {
          toast.error({
            title: intl.formatMessage({ id: 'Banner image must be exactly 2000x920 px.' }),
          });
          clearInput();
          return;
        }
      } catch {
        toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
        clearInput();
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPreviewSrc(reader.result as string);
    };
    reader.readAsDataURL(file);

    onChange(file);
    clearInput();
  };

  return (
    <div className="raffle_image_section">
      <p className="raffle_image_section__title">{title}</p>
      <div className="raffle_image_section__body">
        <div className="raffle_image_section__upload">
          <div className="raffle_image_section__drop_area" onClick={handleOpenFilePicker}>
            {previewSrc ? (
              <Img src={previewSrc} alt={title} />
            ) : (
              <span className="raffle_image_section__placeholder">
                <FormattedMessage id="Click to upload image" />
              </span>
            )}
          </div>
          <div className="raffle_image_section__actions">
            <Button className="select_photo_btn" onClick={handleOpenFilePicker}>
              <FormattedMessage id="Select File" />
            </Button>
            <Button
              className="preview_btn preview_btn--primary"
              onClick={() => setIsPreviewOpen(true)}
              disabled={!previewSrc}
            >
              <FormattedMessage id="App preview" />
            </Button>
          </div>
          {helperText && <p className="raffle_image_section__helper_text">{helperText}</p>}
          <input type="file" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
        </div>
      </div>

      <RaffleImagePreviewModal title={title} visible={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
        <DevicePreview type={type} imageSrc={previewSrc} title={title} />
      </RaffleImagePreviewModal>
    </div>
  );
};

export default RaffleImageSection;
